/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { sortBy } from "lodash";
import { OLAPTransformerResult, useParameter, useDictionary } from "../../../shared";
import { dictionaryService } from "../../../dataFlow";
import { uniq } from "../../../../../shared";
import { TableProps } from "../../../components/Table";
import { ColDef } from "ag-grid-community";
import { SLIDE_NUMBER } from "../constants";
import { getDateParts } from "../utils/dateHelpers";
import { AgGridHeaderComponent } from "../controls/AgGridColumnHeader";

const COLUMNS_AXIS = "commodities.default";
const ROWS_AXIS = "calendar.default";
const UNITS_AXIS = "units.default";
const [YEAR_FREQUENCY, QUARTER_FREQUENCY, MONTH_FREQUENCY] = [1, 3, 4];

export function useAgGridTransformer(olapData: OLAPTransformerResult | null): TableProps | undefined {
  const [state, setState] = useState<TableProps | undefined>();
  const [commodities] = useParameter<number[]>("@commodities");
  const [commoditiesColumns] = useDictionary(dictionaryService, "v_dict_commodities_alternative_price", undefined, {
    attributes: "",
    slide: SLIDE_NUMBER,
  });

  const calendarParameters = useMemo(() => {
    if (olapData != null) {
      return olapData.rows.map((row) => row.attributes[`${ROWS_AXIS}.id`]);
    }
  }, [olapData]);

  const calendarHierarchy = useMemo(() => {
    if (calendarParameters != null && olapData != null) {
      const calendarParts = calendarParameters.map((item) => getDateParts(item));
      const sortedCalendarParts = sortBy(calendarParts, ["year", "month", "frequency"]);

      return sortedCalendarParts.map(({ frequency, year, month, day }) => `${frequency}${year}${month}${day}`);
    }
  }, [calendarParameters, olapData]);

  const categories = useMemo(
    () =>
      uniq(
        commodities?.map<ColDef>((commodityId) => {
          const column = olapData?.columns.find((col) => col.selection[COLUMNS_AXIS] === commodityId);
          const colHeader = column?.headers?.find((header) => header.id === `${COLUMNS_AXIS}.name`);
          let localeName = colHeader?.name as any;
          if (localeName === undefined) {
            const displayItem = commoditiesColumns?.find((item) => item.getValue<number>("id") === commodityId);
            localeName = displayItem?.getValue<any>("name");
          }

          if (typeof localeName === "object") {
            localeName = localeName[9];
          }

          const name = localeName || "";
          return {
            headerName: name,
            field: commodityId.toString(),
            resizable: true,
            minWidth: 100,
            headerComponentFramework: AgGridHeaderComponent,
          };
        }) || ([] as ColDef[]),
      ),
    [olapData, commodities, commoditiesColumns],
  );

  useEffect(() => {
    if (olapData) {
      const rows =
        calendarHierarchy?.map((calendarId) => {
          const dataRow = olapData?.rows.find((row) => Number(row.selection[ROWS_AXIS]) === Number(calendarId));
          const { frequency, year, month } = getDateParts(calendarId);

          let rowName;
          const nFrequency = Number(frequency);
          if (nFrequency === YEAR_FREQUENCY) {
            rowName = year;
          } else if (nFrequency === QUARTER_FREQUENCY) {
            const quarter = Math.floor((Number(month) + 3) / 3);
            rowName = `${quarter} Q. ${year}`;
          } else if (nFrequency === MONTH_FREQUENCY) {
            rowName = `${month}.${year}`;
          }

          const recordsRow = {
            id: calendarId,
            Period: rowName,
          };

          commodities?.forEach((commodityId) => {
            const cell = dataRow?.points.find(
              (point) => point?.attributes && Number(point.attributes[`${COLUMNS_AXIS}.id`]) === commodityId,
            );
            const isEmpty = cell?.value === undefined || cell?.value === null;
            recordsRow[commodityId.toString()] = isEmpty ? "" : cell!.value.toFixed(2);
          });

          return recordsRow;
        }) || [];

      const periodCol: ColDef = {
        headerName: "",
        field: "Period",
        resizable: true,
        minWidth: 140,
        pinned: "left",
      };

      const result: TableProps = {
        columns: [periodCol].concat(categories),
        rows,
        getRowClass: ({ data }) => {
          const { frequency } = getDateParts(data.id);

          const nFrequency = Number(frequency);
          const yearClassName = nFrequency === YEAR_FREQUENCY && "year-row";
          const quarterClassName = nFrequency === QUARTER_FREQUENCY && "quarter-row";
          const monthClassName = nFrequency === MONTH_FREQUENCY && "month-row";

          return [yearClassName, quarterClassName, monthClassName].filter(Boolean).join(" ");
        },
      };

      setState(result);
    } else {
      setState(undefined);
    }
  }, [olapData, calendarParameters]);

  return state;
}
